import { ReactNode, useState } from 'react';
import styled from '@emotion/styled';

import { SideNav } from './SideNav';
import { ChatPanel } from './ChatPanel';
import { TopBar } from './TopBar';
import { SearchOverlay } from './SearchOverlay';

const Page = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: #f6f6f5;
  font-family: 'Flodesk Sans', sans-serif;
  color: #111100;

  button,
  input,
  textarea,
  select {
    font-family: inherit;
  }
`;

const Aside = styled.aside<{ chatOpen: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: ${({ chatOpen }) => (chatOpen ? '512px' : '256px')};
  transition: width 0.32s cubic-bezier(0.4, 0, 0.2, 1);
`;

const Layer = styled.div<{ visible: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  transition: opacity 0.2s ease;
`;

const Main = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const ContentPanel = styled.main`
  flex: 1;
  min-width: 0;
  min-height: 0;
  margin-right: 16px;
  margin-bottom: 16px;
  background: #ffffff;
  border-radius: 24px;
  overflow-x: clip;
  overflow-y: auto;
  position: relative;
`;

type AppShellProps = {
  children: ReactNode;
};

export function AppShell({ children }: AppShellProps) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <Page data-testid="app_shell">
      <Aside chatOpen={chatOpen}>
        <Layer visible={!chatOpen}>
          <SideNav />
        </Layer>
        <Layer visible={chatOpen}>
          <ChatPanel onClose={() => setChatOpen(false)} />
        </Layer>
      </Aside>
      <Main>
        <TopBar
          chatOpen={chatOpen}
          onToggleChat={() => setChatOpen(open => !open)}
          onOpenSearch={() => setSearchOpen(true)}
        />
        <ContentPanel>{children}</ContentPanel>
      </Main>
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} />}
    </Page>
  );
}
